import { ReactNode, FC } from 'react';
import Box from '../../../components/fela/Box';
import componentStyles from './progress-bar.style';

export interface ProgressBarProps {
  /**
   * The color of the progress bar.
   */
  color: string;
  /**
   * The background color of the progress bar.
   */
  backgroundColor: string;
  /**
   * The border color of the progress bar. No border if not provided.
   */
  borderColor?: string;
  /**
   * The progress value (between 0 and `maxProgress`).
   */
  progress: number;
  /**
   * The height of the progress bar.
   */
  height: number;
  /**
   * Optional breakpoints for chapter markers.
   */
  breakpoints?: { label?: string; value: number }[];
  /**
   * The width of the chapter markers.
   */
  chapterWidth?: number;
  /**
   * Whether to show the progress marker.
   */
  showProgressMarker?: boolean;
  /**
   * The maximum progress value.
   */
  maxProgress: number;
  /**
   * Whether to show the minimum value label.
   */
  showMinValue?: boolean;
  /**
   * Whether to show the maximum value label.
   */
  showMaxValue?: boolean;
  /**
   * Determines whether to display the `maxProgress` value with a "+" symbol.
   */
  showMaxValuePlus?: boolean;
  /**
   * The label for the start value.
   */
  startLabel?: string;
  /**
   * The label for the end value.
   */
  endLabel?: string;
  /**
   * Custom calculation breakpoint for positioning.
   */
  customCalculationBreakpoints?: {
    value: number;
    mappedToPercentage: number;
  }[];
  /**
   * Whether to show the values for each breakpoint.
   */
  showBreakPointValues?: boolean;
  /**
   * If provided, displays a custom progress marker.
   * This prop only takes effect if `showProgressMarker` is set to `true`.
   * Otherwise, it is ignored.
   *
   * @requires showProgressMarker
   */
  renderProgressMarker?: (progress: number) => ReactNode;
}

export const ProgressBar: FC<ProgressBarProps> = ({
  color,
  backgroundColor,
  borderColor,
  progress,
  height,
  breakpoints,
  chapterWidth,
  showProgressMarker,
  maxProgress,
  showMinValue,
  showMaxValue,
  showMaxValuePlus,
  startLabel,
  endLabel,
  customCalculationBreakpoints,
  showBreakPointValues = true,
  renderProgressMarker,
}) => {
  const progressNumber = progress >= maxProgress ? maxProgress : progress;
  const breakpointValuesVisible = breakpoints?.length > 0 && showBreakPointValues !== false;

  const getProgressWidth = () => {
    if (progressNumber >= maxProgress) {
      return '100%';
    }

    if (!customCalculationBreakpoints) {
      return `${(progressNumber / maxProgress) * 100}%`;
    }

    let previousBreakpoint = 0;
    let currentPercentage = 0;

    for (const breakpoint of customCalculationBreakpoints) {
      const { value, mappedToPercentage } = breakpoint;

      if (progressNumber === value) {
        currentPercentage = mappedToPercentage;
        break;
      }

      if (progressNumber < value) {
        const previousMappedToPercentage =
          customCalculationBreakpoints.find((bp) => bp.value === previousBreakpoint)
            ?.mappedToPercentage || 0;

        const progressInRange = progressNumber - previousBreakpoint;
        const range = value - previousBreakpoint;
        const percentageInRange = progressInRange / range;

        currentPercentage =
          previousMappedToPercentage +
          percentageInRange * (mappedToPercentage - previousMappedToPercentage);

        break;
      }
      if (customCalculationBreakpoints.length === 1 && progressNumber > value) {
        currentPercentage =
          mappedToPercentage +
          ((progressNumber - value) / (maxProgress - value)) * mappedToPercentage;
        break;
      }

      currentPercentage = mappedToPercentage;
      previousBreakpoint = value;
    }

    return `${currentPercentage}%`;
  };

  const progressEqualsChapter = !!breakpoints?.find((chapter) => chapter.value === progressNumber);

  const styles = componentStyles();

  const renderProgressMarkerElement =
    renderProgressMarker ?? ((p: number) => <Box style={styles.progressMarker(color)}>{p}</Box>);
  return (
    <>
      <div
        style={styles.bar(
          height,
          backgroundColor,
          borderColor,
          showProgressMarker || breakpointValuesVisible,
        )}>
        {breakpoints?.map((chapter, index) => {
          const chapterPercentage = customCalculationBreakpoints?.find(
            (breakpoint) => breakpoint.value === chapter.value,
          )?.mappedToPercentage;

          const chapterMarkerStyle = chapterPercentage
            ? `${chapterPercentage}%`
            : `${(chapter.value / maxProgress) * 100}%`;

          return (
            <div key={index} style={styles.chapterMarker(chapterWidth, chapterMarkerStyle)}>
              <div style={styles.chapterValueWrapper}>
                {breakpointValuesVisible && (
                  <Box style={styles.chapterValue(height)}>{chapter.value}</Box>
                )}
                <Box style={styles.label}>{chapter.label}</Box>
              </div>
            </div>
          );
        })}
        <div
          style={styles.progressStyle(
            color,
            customCalculationBreakpoints
              ? getProgressWidth()
              : `${(progressNumber / maxProgress) * 100}%`,
            progressEqualsChapter,
          )}>
          {showProgressMarker && renderProgressMarkerElement(progress)}
        </div>
      </div>
      {(showMinValue || showMaxValue) && (
        <div style={styles.labelsWrapper}>
          <div>
            {showMinValue && (
              <div>
                <Box style={styles.labelValue}>0</Box>
                <Box style={styles.label}>{startLabel}</Box>
              </div>
            )}
          </div>
          {(showMaxValue || showMaxValuePlus) && (
            <div>
              <Box style={styles.labelValue}>{showMaxValue ? maxProgress : `${maxProgress}+`}</Box>
              <Box style={styles.label}>{endLabel}</Box>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ProgressBar;
